import type { Date as PDate } from "../../generated/core/v1/date";
import { LoanType } from "../../generated/core/v1/debts";
import type { Family } from "../../generated/core/v1/family";
import { PropertyKind } from "../../generated/core/v1/property";
import type {
  DebtsSection_Debt,
  PropertiesSection_PropertyAnswer,
} from "../../generated/core/v1/questionnaire";
import { newUuid } from "../lib/uuid";
import type { DraftDebt, DraftProperty } from "./draft";

/**
 * Properties, mortgages, and other debts as onboarding and Update Data both ask about them (the same
 * questions, labels, and required answers: components/OwedRows.tsx). A blank rate is "not sure": it
 * is sent as not known, never 0, so the engine estimates it, and Home nags until it's filled in
 * (app/pages/missingValues.tsx).
 */

/** A property row as the questionnaire's answer. */
export function propertyAnswer(p: DraftProperty): PropertiesSection_PropertyAnswer {
  return {
    uuid: p.uuid,
    kind: p.kind,
    name: p.name.trim(),
    currentValue: p.value,
    annualPropertyTax: p.propertyTax,
    // unset = owned outright
    mortgage: p.hasMortgage
      ? {
          uuid: p.mortgageUuid,
          balance: p.owing ?? 0,
          interestRate: p.rate,
          // the year it was taken out and its term (the engine amortizes from there); without
          // them a stored mortgage keeps its terms, and a new one gets Abi's default amortization
          ...(p.startYear !== undefined && p.termYears !== undefined
            ? { startYear: p.startYear, termYears: p.termYears }
            : { monthlyPayment: p.monthlyPayment }),
          lender: p.lender ?? 0,
        }
      : undefined,
  };
}

/** A debt row as the questionnaire's answer; `owner` is whose it is when the row doesn't say. */
export function debtAnswer(d: DraftDebt, owner: string): DebtsSection_Debt {
  return {
    uuid: d.uuid,
    kind: d.kind,
    name: d.name.trim(),
    memberUuid: d.memberUuid || owner,
    balance: d.balance ?? 0,
    interestRate: d.rate,
    monthlyPayment: d.monthlyPayment,
    creditLimit: d.kind === LoanType.CREDIT_CARD ? d.creditLimit : undefined,
  };
}

/** The family's properties (each with its mortgage, if any) as rows to edit. */
export function propertyRowsFromFamily(family: Family): DraftProperty[] {
  return family.properties.map((p) => {
    const m = family.mortgages.find((x) => x.propertyUuid === p.uuid);
    return {
      uuid: p.uuid,
      // a property stored without a kind (from before the redesign) is the home
      kind: p.kind || PropertyKind.PRINCIPAL_RESIDENCE,
      name: p.name,
      value: p.currentValue,
      propertyTax: p.annualPropertyTax,
      hasMortgage: !!m,
      mortgageUuid: m?.uuid ?? newUuid(),
      owing: m?.balance,
      rate: m?.interest,
      // the year and the term as given; a mortgage stored with years left (before v40) or a payment
      // shows them blank, with what is stored as a hint (components/OwedRows.tsx)
      startYear:
        m?.terms.oneofKind === "amortizationPeriod" && m.purchaseDate?.year
          ? m.purchaseDate.year
          : undefined,
      termYears:
        m?.terms.oneofKind === "amortizationPeriod" && m.purchaseDate?.year
          ? m.terms.amortizationPeriod
          : undefined,
      remainingMonths:
        m?.terms.oneofKind === "remainingAmortizationMonths"
          ? m.terms.remainingAmortizationMonths
          : undefined,
      monthlyPayment:
        m?.terms.oneofKind === "existingMonthlyPayment"
          ? m.terms.existingMonthlyPayment
          : undefined,
      lender: m?.lender,
    };
  });
}

/** The family's credit cards and loans as rows to edit. */
export function debtRowsFromFamily(family: Family): DraftDebt[] {
  return [
    ...family.persons.flatMap((p) =>
      p.creditCards.map((c) => ({
        uuid: c.uuid,
        kind: LoanType.CREDIT_CARD,
        name: c.name,
        memberUuid: p.uuid,
        balance: c.balance,
        rate: c.interest,
        monthlyPayment: c.existingMonthlyPayment,
        creditLimit: c.creditLimit,
      })),
    ),
    ...family.loans.map((l) => ({
      uuid: l.uuid,
      kind: l.type || LoanType.OTHER,
      name: l.name,
      balance: l.balance,
      rate: l.interest,
      monthlyPayment: l.existingMonthlyPayment,
    })),
  ];
}

export type OwedIssue = { path: string; message: string };

/**
 * The presence and range checks both flows make (not financial rules): a property's current value
 * and a mortgage's or debt's amount owing are required; a rate may be blank ("not sure"); a
 * mortgage's start year and term go together, the year no later than this one, and the two must
 * reach past this year (the engine refuses a mortgage that would already be paid off).
 */
export function validateOwed(
  properties: DraftProperty[],
  debts: DraftDebt[],
  today?: PDate,
): OwedIssue[] {
  const thisYear = today?.year || new Date().getFullYear();
  const out: OwedIssue[] = [];
  const amount = (path: string, v: number | undefined, required: boolean) => {
    if (v === undefined) {
      if (required) out.push({ path, message: "Required" });
    } else if (v < 0) out.push({ path, message: "Enter a positive amount" });
  };
  const rate = (path: string, v: number | undefined) => {
    if (v !== undefined && (v < 0 || v > 100))
      out.push({ path, message: "Enter a rate between 0 and 100" });
  };
  properties.forEach((p, i) => {
    amount(`properties.${i}.value`, p.value, true);
    amount(`properties.${i}.propertyTax`, p.propertyTax, false);
    if (!p.hasMortgage) return;
    amount(`properties.${i}.owing`, p.owing, true);
    rate(`properties.${i}.rate`, p.rate);
    const { startYear: start, termYears: term } = p;
    if (start !== undefined && (start < 1950 || start > thisYear || !Number.isInteger(start)))
      out.push({ path: `properties.${i}.startYear`, message: `A year from 1950 to ${thisYear}` });
    else if (term !== undefined && (term <= 0 || term > 40 || !Number.isInteger(term)))
      out.push({ path: `properties.${i}.termYears`, message: "Between 1 and 40 years" });
    else if (start === undefined && term !== undefined)
      out.push({ path: `properties.${i}.startYear`, message: "And the year you took it out" });
    else if (start !== undefined && term === undefined)
      out.push({ path: `properties.${i}.termYears`, message: "And how many years it was for" });
    else if (start !== undefined && term !== undefined && start + term <= thisYear)
      out.push({
        path: `properties.${i}.termYears`,
        message: `That would be paid off by ${start + term}. Check the year and the term.`,
      });
  });
  debts.forEach((d, i) => {
    amount(`debts.${i}.balance`, d.balance, true);
    rate(`debts.${i}.rate`, d.rate);
  });
  return out;
}
